import { List, ListItem, Typography, styled } from "@mui/material";
import clsx from "clsx";
import moment from "moment";
import CollapsibleSection from "../../../components/collapsible-section/collapsible-section";
import DownloadLocations from "../../../components/download-locations/download-locations";
import { displayDateFormat } from "../../../constants";
import { downloadSubListLocationsAction } from "../../../features/download/redux/download.actions";
import { IRootState } from "../../../redux/root.reducer";
import { useAppSelector } from "../../../redux/store";
import { canAccessStubRules } from "../../../service/user-permission";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import SublistItemCell from "./sublist-item-cell";

const Sublists = ({ className }: IWithClassName) => {
    const { data, isLoading } = useAppSelector(
        ({ listPage }) => listPage.sublist,
    );
    const { isLoading: isLoadingListDetails } = useAppSelector(
        ({ listPage }) => listPage.listDetails,
    );
    const role = useAppSelector(({ auth }: IRootState) => auth.user?.role);
    const hasStubAccess = canAccessStubRules(role);

    return !isLoading && !data?.length ? (
        <></>
    ) : (
        <CollapsibleSection
            className={className}
            title="Sublists"
            collapsible={true}
            collapsed={true}
            isLoading={isLoading || isLoadingListDetails}
        >
            <ListItem className="sublist-title">
                <Typography className="sublist-id">List ID</Typography>
                <Typography className="sublist-name">List Name</Typography>
                {hasStubAccess && (
                    <Typography className="stub-name">Stub Name</Typography>
                )}
                <Typography className="sublist-location">Locations</Typography>
                <Typography className="sublist-last-updated">
                    Last Updated
                </Typography>
                <Typography className="sublist-download">Download</Typography>
            </ListItem>
            <List>
                {data?.map((sublist) => (
                    <ListItem key={sublist.listId} className="sublist-item">
                        <SublistItemCell
                            disabled={!sublist.hasAccess}
                            listId={sublist.listId}
                            className={clsx("sublist-id", {
                                disabled: !sublist.hasAccess,
                            })}
                        >
                            {sublist.listId}
                        </SublistItemCell>
                        <SublistItemCell
                            disabled={!sublist.hasAccess}
                            listId={sublist.listId}
                            className={clsx("sublist-name", {
                                disabled: !sublist.hasAccess,
                            })}
                        >
                            {sublist.name}
                        </SublistItemCell>
                        {hasStubAccess && (
                            <SublistItemCell
                                className={clsx("stub-name", {
                                    disabled: !sublist.hasAccess,
                                })}
                                disabled={!sublist.hasAccess}
                                listId={sublist.listId}
                            >
                                {sublist.stub_name}
                            </SublistItemCell>
                        )}
                        <SublistItemCell
                            disabled={!sublist.hasAccess}
                            listId={sublist.listId}
                            className={clsx("sublist-location", {
                                disabled: !sublist.hasAccess,
                            })}
                        >
                            {sublist.records}
                        </SublistItemCell>
                        <SublistItemCell
                            disabled={!sublist.hasAccess}
                            listId={sublist.listId}
                            className={clsx("sublist-last-updated", {
                                disabled: !sublist.hasAccess,
                            })}
                        >
                            {sublist.lastUpdated
                                ? moment
                                      .utc(sublist.lastUpdated)
                                      .format(displayDateFormat)
                                : "-"}
                        </SublistItemCell>
                        <DownloadLocations
                            disabled={!sublist.hasAccess}
                            label=""
                            className="sublist-download"
                            downloadAction={() =>
                                downloadSubListLocationsAction(sublist.listId)
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </CollapsibleSection>
    );
};

export default styled(Sublists)(({ theme }: IWithThemeProps) => ({
    paddingTop: theme?.spacing(3),
    fontSize: 20,
    color: theme?.palette.announcement.primaryColor,

    ".disabled": {
        opacity: 0.3,
    },

    ".MuiListItem-root": {
        color: theme?.palette.sublists.textColor,
        padding: theme?.spacing(4, 0),
        height: 80,
        borderBottom: `1px solid ${
            theme?.palette.detailsPane.borderBottom ?? ""
        }  `,
    },
    ".MuiListItem-root:last-child": {
        border: "none",
    },
    ".sublist-title": {
        p: {
            fontWeight: 600,
        },
    },
    ".sublist-id": {
        flexBasis: 100,
        flexGrow: 0,
        flexShrink: 0,
        paddingLeft: theme?.spacing(5),
    },
    ".sublist-name": {
        flexBasis: "50%",
        flexGrow: 1,
    },
    ".stub-name": {
        flexBasis: "50%",
        flexGrow: 1,
    },
    ".sublist-location": {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 120,
    },
    ".sublist-last-updated": {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 100,
    },
    ".sublist-download": {
        flexBasis: 100,
        flexShrink: 0,
        flexGrow: 0,
        textAlign: "right",
        paddingRight: theme?.spacing(4),
        justifyContent: "right",
        fontSize: 16,
    },
}));
